import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useAuth } from './AuthContext';

const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const { user: authUser, isLoading: authLoading } = useAuth();
  
  // Sync currentUser with AuthContext user
  const [currentUser, setCurrentUser] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [isReady, setIsReady] = useState(false); // NEW: Track if UserContext is ready

  // Sync UserContext with AuthContext
  useEffect(() => {
    console.log('🔄 UserContext: Syncing with AuthContext, authLoading:', authLoading, 'authUser:', authUser?.email);
    
    // Don't sync while AuthContext is still loading
    if (authLoading) {
      setIsReady(false);
      return;
    }
    
    if (authUser) {
      setCurrentUser(authUser);
      setCurrentUserId(authUser.id);
      setIsReady(true); // ✅ Ready after user is set
      console.log('✅ UserContext synced with AuthContext:', authUser.name, 'ID:', authUser.id);
    } else {
      // NESSUN FALLBACK - se non c'è utente autenticato, tutto null
      setCurrentUser(null);
      setCurrentUserId(null);
      setIsReady(true); // ✅ Ready even if no user (for login page)
      console.log('🚫 UserContext: No authenticated user - all data access blocked');
    }
  }, [authUser, authLoading]);

  // API helpers using actual logged-in user
  const getApiHeaders = () => {
    if (!currentUserId) {
      throw new Error('Utente non autenticato - accesso negato');
    }
    return {
      'Content-Type': 'application/json',
      'X-User-ID': currentUserId // SOLO user ID autenticato, nessun fallback
    };
  };

  const apiCall = useCallback(async (url, options = {}) => {
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const fullUrl = url.startsWith('http') ? url : `${backendUrl}/api${url}`;
    
    const response = await fetch(fullUrl, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-User-ID': currentUserId,
        ...options.headers
      }
    });
    
    // Return the response object, not the promise
    return response;
  }, [currentUserId]);

  const switchUser = (user) => {
    setCurrentUser(user);
    setCurrentUserId(user.id);
  };

  const value = {
    currentUserId: currentUserId,
    currentUser,
    setCurrentUser: switchUser,
    getApiHeaders,
    apiCall,
    isReady // NEW: Expose isReady flag
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  // Fallback context (non dovrebbe essere usato se provider è corretto)
  if (!context) {
    console.error('useUser must be used within a UserProvider');
    return {
      currentUserId: null, // NESSUN fallback hardcoded
      currentUser: null,
      setCurrentUser: () => {},
      getApiHeaders: () => { throw new Error('UserContext not available - user not authenticated'); },
      apiCall: () => Promise.reject('UserContext not available - user not authenticated'),
      isReady: false // NEW
    };
  }
  return context;
};

export default UserContext;